import React from "react";
import ReactDOM from "react-dom";
import { FiSun, FiMoon, FiMonitor } from "react-icons/fi";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { useQueryClient } from "@tanstack/react-query";

import styles from "./Modal.module.scss";
import {
  importThreadsFromJson,
  type ImportProgress,
  type ImportResult,
} from "services/dbApi/utilities/exportImport";
import {
  IDB_MIGRATION_COMPLETED_KEY,
  IDB_MIGRATION_DISMISSED_KEY,
} from "components/IdbMigration/flags";
import ImportProgressOverlay from "./ImportProgressOverlay";
import { useThemeState, type Theme } from "../ThemeSelector/useThemeState";
import {
  useChatWidthState,
  type ChatWidth,
} from "../ThemeSelector/useChatWidthState";
import { useSubagentState } from "../ThemeSelector/useSubagentState";

interface SettingsModalProps {
  onClose: () => void;
  onDeleteAllChats: () => void;
  onExportCurrentChat?: () => void;
  /**
   * Used to authenticate calls during export/import.
   */
  getAccessToken: () => Promise<string>;
}

const themeOptions: { value: Theme; label: string; icon: React.ReactNode }[] = [
  { value: "light", label: "Light", icon: <FiSun /> },
  { value: "system", label: "System", icon: <FiMonitor /> },
  { value: "dark", label: "Dark", icon: <FiMoon /> },
];

const chatWidthOptions: { value: ChatWidth; label: string }[] = [
  { value: "standard", label: "Standard" },
  { value: "wide", label: "Wide" },
];

export default function SettingsModal({
  onClose,
  onDeleteAllChats,
  onExportCurrentChat,
  getAccessToken,
}: SettingsModalProps) {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const [theme, setTheme] = useThemeState();
  const [chatWidth, setChatWidth] = useChatWidthState();
  const [subagentEnabled, setSubagentEnabled] = useSubagentState();
  // While `progress !== null`, the import-progress overlay is mounted and
  // the chat-management buttons are disabled. `result` flips to non-null
  // once the import finishes — that's the overlay's "done" state.
  const [progress, setProgress] = React.useState<ImportProgress | null>(null);
  const [result, setResult] = React.useState<ImportResult | null>(null);
  const isImporting = progress !== null;
  // <IdbMigration> sets IDB_MIGRATION_COMPLETED_KEY when migration succeeds OR when
  // there's nothing in IDB to migrate, so a single synchronous read tells
  // us whether to show the "Migrate IDB" button.
  const showMigrateIdb =
    localStorage.getItem(IDB_MIGRATION_COMPLETED_KEY) !== "true";

  const handleDeleteAllChats = () => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete all chats? This action cannot be undone."
    );
    if (confirmDelete) {
      onDeleteAllChats();
      onClose();
    }
  };

  const handleImportChats = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const confirmImport = window.confirm(
      "Importing will add threads from the file to your chat history. Threads with ids that already exist on the server are skipped. Continue?"
    );
    if (!confirmImport) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    // Mount the overlay with a zeroed state. The first onProgress emit from
    // importThreadsFromJson fills in `total` once the file has been parsed.
    setProgress({
      total: 0,
      imported: 0,
      skipped: 0,
      failed: 0,
      currentThreadName: "",
    });

    try {
      const importResult = await importThreadsFromJson(
        file,
        getAccessToken,
        setProgress
      );
      setResult(importResult);
    } catch (error) {
      console.error("Failed to import chats:", error);
      alert("Failed to import chats. Please ensure the file is valid JSON.");
      setProgress(null);
      setResult(null);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Drives the user back into <IdbMigration>: clearing the dismissed flag
  // makes the gate re-show on the next mount, so we just reload. The single
  // migration code path lives in the gate.
  const handleMigrateIdb = () => {
    localStorage.removeItem(IDB_MIGRATION_DISMISSED_KEY);
    window.location.reload();
  };

  // Called from the overlay's "Close" button (only available after the
  // import finishes). Refreshes the sidebar and dismisses everything.
  const handleImportOverlayClose = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["cc-db-api", "threads"],
    });
    setProgress(null);
    setResult(null);
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <>
      <div className={styles.backdrop} onClick={handleBackdropClick}>
        <div className={styles.modal}>
          <div className={styles.header}>
            <h2 className={styles.sectionTitle}>Settings</h2>
            <button
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Close settings"
            >
              ✕
            </button>
          </div>

          <div className={styles.content}>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Appearance</h3>
              <p className={styles.sectionDescription}>Theme</p>
              <div className={styles.themeSelector}>
                {themeOptions.map((option) => (
                  <button
                    key={option.value}
                    className={`${styles.themeOption} ${
                      theme === option.value ? styles.themeOptionActive : ""
                    }`}
                    onClick={() => setTheme(option.value)}
                    aria-pressed={theme === option.value}
                  >
                    {option.icon}
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
              <p
                className={styles.sectionDescription}
                style={{ marginTop: "1rem" }}
              >
                Chat width
              </p>
              <div className={styles.themeSelector}>
                {chatWidthOptions.map((option) => (
                  <button
                    key={option.value}
                    className={`${styles.themeOption} ${
                      chatWidth === option.value ? styles.themeOptionActive : ""
                    }`}
                    onClick={() => setChatWidth(option.value)}
                    aria-pressed={chatWidth === option.value}
                  >
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
              <p
                className={styles.sectionDescription}
                style={{ marginTop: "1rem" }}
              >
                Allow Subagents{" "}
                <em style={{ fontWeight: 300, opacity: 0.7 }}>
                  (Technical Preview)
                </em>{" "}
                <Tippy
                  content="Technical Preview: This feature is experimental and may change. Delegate Subagent lets the assistant offload complex, multi-step work to a background sub-agent and return later with results. Only applies to personas that support it; other personas ignore this setting."
                  placement="top"
                >
                  <span style={{ cursor: "help" }}>
                    <FontAwesomeIcon icon={faCircleInfo} />
                  </span>
                </Tippy>
              </p>
              <div className={styles.themeSelector}>
                {(["on", "off"] as const).map((value) => (
                  <button
                    key={value}
                    className={`${styles.themeOption} ${
                      subagentEnabled === value ? styles.themeOptionActive : ""
                    }`}
                    onClick={() => setSubagentEnabled(value)}
                    aria-pressed={subagentEnabled === value}
                  >
                    <span>{value === "on" ? "On" : "Off"}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Chat Management</h3>
              <p className={styles.sectionDescription}>
                Manage your chat history and data.
              </p>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                {onExportCurrentChat && (
                  <button
                    className={styles.button}
                    onClick={onExportCurrentChat}
                    disabled={isImporting}
                  >
                    Export Current Chat
                  </button>
                )}
                <button
                  className={styles.button}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isImporting}
                >
                  {isImporting ? "Importing…" : "Import Chats"}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json,application/json"
                  onChange={handleImportChats}
                  style={{ display: "none" }}
                />
                {showMigrateIdb && (
                  <button
                    className={styles.button}
                    onClick={handleMigrateIdb}
                    disabled={isImporting}
                  >
                    Migrate IDB
                  </button>
                )}
                <button
                  className={styles.dangerButton}
                  onClick={handleDeleteAllChats}
                  disabled={isImporting}
                >
                  Delete All Chats
                </button>
              </div>
              <p className={styles.smallprint}>
                Note: Import/Export is an experimental feature.
              </p>
            </div>
          </div>

          <div className={styles.footer}>
            <button className={styles.secondaryButton} onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
      {progress !== null && (
        <ImportProgressOverlay
          progress={progress}
          result={result ?? undefined}
          onClose={handleImportOverlayClose}
        />
      )}
    </>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
